import { useMemo, useState } from 'react';
import { Text } from '../../../components/typography';
import TextWithIcon from '../../../components/typography/TextWithIcon';
import { evaluateCondition } from '../../../utilities/inspection';
import { actionsKeys } from '../../../components/question_menu/conditionalActions/conditionalAction.utils';
import { customColors } from '../../../utilities/misc';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import QuestionNoAuth from './QuestionNoAuth';
import styles from './ConditionalInspectionItemNoAuth.module.scss';
import findFileIcon from '../../../utilities/files';
import { getExtension } from '../../../components/files/utils';
import RichTextEditor from '../../../components/rich_text/RichTextEditor';

export default function ConditionalInspectionItemNoAuth({
  question,
  localAnswers,
  onLocalAnswer,
  company,
}) {
  const { t } = useTranslation();
  const { conditionalAction = [], inputType } = question || {};
  const answerValue = localAnswers[question?.id]?.value;

  const matchingActions = useMemo(() => {
    const conditions = Array.isArray(conditionalAction)
      ? conditionalAction
      : [];
    return conditions
      .filter(
        (ca) =>
          answerValue &&
          evaluateCondition(answerValue, { inputType, conditionalAction: ca }),
      )
      .flatMap((ca) => ca.actions ?? []);
  }, [conditionalAction, inputType, answerValue]);

  const actionsToRender = { questions: [], messages: [], files: [] };

  for (const { action, actionable, additionalData } of matchingActions) {
    switch (action) {
      case actionsKeys.followUpQuestion:
        if (actionable?.id) actionsToRender.questions.push(actionable);
        break;
      case actionsKeys.showMessage:
      case actionsKeys.popUp:
        actionsToRender.messages.push({ ...additionalData, action });
        break;
      case actionsKeys.showFile:
        if (actionable?.id) actionsToRender.files.push(actionable);
        break;
      // SEND_NOTIFICATION and FOLLOW_UP_TEMPLATE are handled server-side or
      // not applicable for noAuth users — intentionally skipped here
      default:
        break;
    }
  }

  const { questions, messages, files } = actionsToRender;

  if (!matchingActions.length) return null;

  return (
    <div className={styles.container}>
      {questions.length > 0 && (
        <div>
          <Text noMargin weight="semiBold" size="sm">
            {t(keys.conditionalActions.QUESTIONS)}
          </Text>
          <div className={styles.list}>
            {questions.map((actionable, idx) => (
              <QuestionNoAuth
                key={`conditional-q-${actionable.id}`}
                index={idx}
                inspectionId={null}
                question={actionable}
                onLocalAnswer={onLocalAnswer}
                company={company}
              />
            ))}
          </div>
        </div>
      )}
      {files.length > 0 && (
        <div className={styles.border}>
          <Text noMargin weight="semiBold">
            {t(keys.conditionalActions.FILES)}
          </Text>
          <Text noMargin size="sm" color="secondary">
            {t(keys.conditionalActions.FILES_SUBTEXT)}
          </Text>
          <div className={styles.list}>
            {files.map((file) => (
              <a
                key={`file-${file.id}`}
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className={styles.fileCard}
              >
                <img
                  className={styles.fileIcon}
                  alt={file.name}
                  src={findFileIcon(getExtension(file.url ?? file.name))}
                />
                <Text noMargin weight="semiBold">
                  {file.name}
                </Text>
              </a>
            ))}
          </div>
        </div>
      )}
      {messages.length > 0 && (
        <div className={styles.border}>
          <TextWithIcon
            size="lg"
            icon="announcement"
            iconProps={{ color: customColors.YELLOW }}
            noMargin
            weight="semiBold"
          >
            {t(keys.conditionalActions.MESSAGES)}
          </TextWithIcon>
          <div className={styles.list}>
            {messages.map(({ title, message }, idx) => (
              <MessageBanner key={idx} title={title} message={message} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBanner({ title, message }) {
  const [expanded, setExpanded] = useState(!!title);

  if (!title) {
    return <RichTextEditor readOnly value={message} />;
  }

  return (
    <div className={styles.messageBanner}>
      <Text
        noMargin
        weight="semiBold"
        color="accentPrimary"
        hover
        onClick={() => setExpanded((p) => !p)}
      >
        {title}
      </Text>
      {expanded && message && (
        <Text noMargin size="sm">
          {message}
        </Text>
      )}
    </div>
  );
}
